import React, { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { Label } from '@/components/ui/label'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { FormField } from '@/components/forms/FormField'
import { FormTextarea } from '@/components/forms/FormTextarea'
import { FormSelect } from '@/components/forms/FormSelect'
import { useDepartments } from '@/hooks/useWorkforce'
import { useTenant } from '@/lib/tenant/tenant-provider'
import { useCreateRole, useUpdateRole } from '@/hooks/useWorkforceMutations'
import { listRoleGroups } from '@/api/workforce'
import { roleAPISchema, cleanFormData } from '@/lib/validations/workforce'
import { RoleGroupSelect } from './RoleGroupSelect'
import type { Role, RoleWithDetails, CreateRoleData } from '@/types/workforce'
import { Save, X, Plus, Trash2 } from 'lucide-react'
import { z } from 'zod'

interface RoleFormProps {
  role?: RoleWithDetails
  mode: 'create' | 'edit'
}

type RoleFormData = z.infer<typeof roleAPISchema>

const LEVEL_OPTIONS = [
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Mid-level' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
  { value: 'manager', label: 'Manager' },
  { value: 'director', label: 'Director' },
  { value: 'executive', label: 'Executive' },
]

export function RoleForm({ role, mode }: RoleFormProps) {
  const navigate = useNavigate()
  const { tenantDomain } = useTenant()

  // Local state for array fields
  const [responsibilities, setResponsibilities] = useState<string[]>(role?.responsibilities || [])
  const [requirements, setRequirements] = useState<string[]>(role?.requirements || [])
  const [newResponsibility, setNewResponsibility] = useState('')
  const [newRequirement, setNewRequirement] = useState('')

  // Role group state
  const [roleGroup, setRoleGroup] = useState<string | null>(role?.roleGroup || null)
  const [existingGroups, setExistingGroups] = useState<string[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)

  // Fetch existing role groups
  useEffect(() => {
    const fetchGroups = async () => {
      if (!tenantDomain) return
      setLoadingGroups(true)
      try {
        const groups = await listRoleGroups(tenantDomain)
        setExistingGroups(groups)
      } catch (err) {
        console.error('Failed to fetch role groups:', err)
      } finally {
        setLoadingGroups(false)
      }
    }
    fetchGroups()
  }, [tenantDomain])

  // ✨ React Hook Form with Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<RoleFormData>({
    resolver: zodResolver(roleAPISchema) as any,
    mode: 'onBlur', // Validate on blur for better UX
    defaultValues: {
      name: role?.name ?? '',
      description: role?.description ?? '',
      level: (role?.level ?? 'mid') as any,
      departmentId: role?.departmentId,
      salary: role?.salary ?? undefined,
    },
  })

  // Server state
  const { departments, loading: depsLoading } = useDepartments()

  // Mutations
  const createRole = useCreateRole()
  const updateRole = useUpdateRole()

  const isLoading = isSubmitting || createRole.isPending || updateRole.isPending

  // Form submission
  const onSubmit = async (data: RoleFormData) => {
    try {
      const cleanedData: CreateRoleData = cleanFormData({
        ...data,
        roleGroup: roleGroup || undefined,
        responsibilities: responsibilities.length > 0 ? responsibilities : undefined,
        requirements: requirements.length > 0 ? requirements : undefined,
      })

      if (mode === 'create') {
        await createRole.mutateAsync(cleanedData)
        navigate({ to: '/workforce/roles' })
      } else if (role) {
        await updateRole.mutateAsync({
          roleId: role.roleId,
          updates: cleanedData,
        })
        navigate({ to: `/workforce/roles/${role.roleId}` as any })
      }
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleCancel = () => {
    if (mode === 'edit' && role) {
      navigate({ to: `/workforce/roles/${role.roleId}` as any })
    } else {
      navigate({ to: '/workforce/roles' as any })
    }
  }

  // Array field handlers
  const addResponsibility = () => {
    if (newResponsibility.trim()) {
      setResponsibilities((prev) => [...prev, newResponsibility.trim()])
      setNewResponsibility('')
    }
  }

  const removeResponsibility = (index: number) => {
    setResponsibilities((prev) => prev.filter((_, i) => i !== index))
  }

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setRequirements((prev) => [...prev, newRequirement.trim()])
      setNewRequirement('')
    }
  }

  const removeRequirement = (index: number) => {
    setRequirements((prev) => prev.filter((_, i) => i !== index))
  }

  const departmentOptions = departments.map((dept) => ({
    value: dept.departmentId,
    label: dept.name,
  }))

  if (depsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-6">
      {/* Basic Information */}
      <Card className="p-6">
        <h2 className="font-display text-xl font-semibold text-white mb-6">
          Informações Básicas
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div className="md:col-span-2">
            <FormField
              label="Nome do Cargo"
              name="name"
              register={register}
              error={errors.name}
              required
              placeholder="Ex: Desenvolvedor Full Stack"
            />
          </div>

          {/* Level */}
          <FormSelect
            label="Nível"
            name="level"
            register={register}
            error={errors.level}
            required
            options={LEVEL_OPTIONS}
          />

          {/* Department */}
          <FormSelect
            label="Departamento"
            name="departmentId"
            register={register}
            error={errors.departmentId}
            options={departmentOptions}
            placeholder="Nenhum"
          />

          {/* Role Group */}
          <div>
            <Label className="block text-sm font-medium text-brand-gray mb-2">
              Grupo de Funções
            </Label>
            <RoleGroupSelect
              value={roleGroup}
              onChange={setRoleGroup}
              existingGroups={existingGroups}
              disabled={loadingGroups}
              placeholder="Selecione ou crie um grupo..."
            />
            <p className="text-xs text-brand-gray mt-1">
              Agrupa funções similares para facilitar filtros (ex: Diretores, Coordenadores)
            </p>
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <FormTextarea
              label="Descrição"
              name="description"
              register={register}
              error={errors.description}
              placeholder="Descreva o cargo e suas principais atribuições..."
              rows={4}
            />
          </div>
        </div>
      </Card>

      {/* Salary Range */}
      <Card className="p-6">
        <h2 className="font-display text-xl font-semibold text-white mb-6">
          Faixa Salarial
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField
            label="Salário Mínimo (R$)"
            name="salary.min"
            type="number"
            register={register}
            error={errors.salary?.min}
            placeholder="0"
            min={0}
            step={0.01}
          />

          <FormField
            label="Salário Máximo (R$)"
            name="salary.max"
            type="number"
            register={register}
            error={errors.salary?.max}
            placeholder="0"
            min={0}
            step={0.01}
          />

          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">Moeda</label>
            <Input value="BRL" disabled />
          </div>
        </div>
      </Card>

      {/* Responsibilities */}
      <Card className="p-6">
        <h2 className="font-display text-xl font-semibold text-white mb-6">
          Responsabilidades
        </h2>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={newResponsibility}
              onChange={(e) => setNewResponsibility(e.target.value)}
              placeholder="Digite uma responsabilidade..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addResponsibility())}
            />
            <Button type="button" onClick={addResponsibility} size="sm">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-2">
            {responsibilities.map((resp, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-3 bg-brand-dark/50 rounded-monumenta"
              >
                <span className="flex-1 text-white">{resp}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeResponsibility(index)}
                >
                  <Trash2 className="w-4 h-4 text-error" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Requirements */}
      <Card className="p-6">
        <h2 className="font-display text-xl font-semibold text-white mb-6">Requisitos</h2>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={newRequirement}
              onChange={(e) => setNewRequirement(e.target.value)}
              placeholder="Digite um requisito..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
            />
            <Button type="button" onClick={addRequirement} size="sm">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-2">
            {requirements.map((req, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-3 bg-brand-dark/50 rounded-monumenta"
              >
                <span className="flex-1 text-white">{req}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRequirement(index)}
                >
                  <Trash2 className="w-4 h-4 text-error" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Form Actions */}
      <Card className="p-6">
        <div className="flex items-center justify-end gap-4">
          <Button type="button" variant="ghost" onClick={handleCancel} disabled={isLoading}>
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <LoadingSpinner className="mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {mode === 'create' ? 'Criar Cargo' : 'Salvar Alterações'}
          </Button>
        </div>
      </Card>
    </form>
  )
}
